import { useState, useMemo, useCallback, useEffect } from 'react';
import { SubscriptionSummary } from '../../../../../types/subscription';
import { DistributionEntry } from '../../../types';
import { Exchange } from 'grts-utils/types/Exchange';

function buildEntries(
  subscriptions: SubscriptionSummary[],
  selectedExchange: Exchange | null,
): DistributionEntry[] {
  const clusterTotal = subscriptions.reduce(
    (sum, s) => sum + s.totalBalanceInStable,
    0,
  );

  return subscriptions.map((s) => {
    const isDisabled = s.exchange !== selectedExchange;
    const pct =
      clusterTotal === 0 ? 0 : (s.totalBalanceInStable / clusterTotal) * 100;

    const latestOrderType = s.latestTransaction?.orderType as
      | 'BUY'
      | 'SELL'
      | null;

    return {
      subscriptionId: s.id,
      botFriendlyName: s.botFriendlyName,
      baseAsset: s.baseAsset,
      quoteAsset: s.quoteAsset,
      exchange: s.exchange,
      latestPrice: s.latestPrice,
      latestOrderType: latestOrderType ?? null,
      originalPercentage: pct,
      originalTotalBalance: s.totalBalanceInStable,
      originalStableBalance: s.stableBalance,
      originalAssetBalance: s.assetBalance,
      currentPercentage: pct,
      currentTotalBalance: s.totalBalanceInStable,
      currentStableBalance: s.stableBalance,
      currentAssetBalance: s.assetBalance,
      isLocked: true,
      isDisabled,
    };
  });
}

function splitBalanceChange(
  entry: DistributionEntry,
  newTotal: number,
): { stableBalance: number; assetBalance: number } {
  const diff = newTotal - entry.originalTotalBalance;
  const price = entry.latestPrice;

  if (diff === 0 || price === 0) {
    return {
      stableBalance: entry.originalStableBalance,
      assetBalance: entry.originalAssetBalance,
    };
  }

  let prioritizeAsset: boolean;
  if (entry.latestOrderType === 'SELL') {
    prioritizeAsset = true;
  } else if (entry.latestOrderType === 'BUY') {
    prioritizeAsset = false;
  } else {
    const assetValueInStable = entry.originalAssetBalance * price;
    prioritizeAsset = assetValueInStable >= entry.originalStableBalance;
  }

  let newStable = entry.originalStableBalance;
  let newAsset = entry.originalAssetBalance;

  if (diff < 0) {
    const absLoss = Math.abs(diff);
    if (prioritizeAsset) {
      const maxAssetLoss = newAsset * price;
      const assetLoss = Math.min(absLoss, maxAssetLoss);
      const stableLoss = absLoss - assetLoss;
      newAsset = newAsset - assetLoss / price;
      newStable = newStable - stableLoss;
    } else {
      const maxStableLoss = newStable;
      const stableLoss = Math.min(absLoss, maxStableLoss);
      const assetLoss = absLoss - stableLoss;
      newStable = newStable - stableLoss;
      newAsset = newAsset - assetLoss / price;
    }
  } else {
    if (prioritizeAsset) {
      newAsset = newAsset + diff / price;
    } else {
      newStable = newStable + diff;
    }
  }

  return {
    stableBalance: Math.max(0, newStable),
    assetBalance: Math.max(0, newAsset),
  };
}

/**
 * Distributes a delta equally across eligible subs, clamping each to [0, 100].
 * Returns a map of subscriptionId -> percentage change to apply.
 */
function distributeAcross(
  entries: DistributionEntry[],
  eligible: DistributionEntry[],
  delta: number,
): Map<number, number> {
  const shares = new Map<number, number>();
  let toDistribute = -delta;
  let activeSubs = eligible.map((e) => e.subscriptionId);

  while (Math.abs(toDistribute) > 0.001 && activeSubs.length > 0) {
    const perSub = toDistribute / activeSubs.length;
    let overflow = 0;
    const nextActive: number[] = [];

    for (const id of activeSubs) {
      const e = entries.find((x) => x.subscriptionId === id)!;
      const accumulated = shares.get(id) || 0;
      const resultPct = e.currentPercentage + accumulated + perSub;

      if (resultPct < -0.001) {
        shares.set(id, -e.currentPercentage);
        overflow += resultPct;
      } else if (resultPct > 100.001) {
        shares.set(id, 100 - e.currentPercentage);
        overflow += resultPct - 100;
      } else {
        shares.set(id, accumulated + perSub);
        nextActive.push(id);
      }
    }

    if (nextActive.length === activeSubs.length) break;
    toDistribute = overflow;
    activeSubs = nextActive;
  }

  return shares;
}

function applyShares(
  entries: DistributionEntry[],
  shares: Map<number, number>,
  clusterTotal: number,
): DistributionEntry[] {
  return entries.map((e) => {
    if (!shares.has(e.subscriptionId)) return e;
    const share = shares.get(e.subscriptionId)!;
    const newPct = Math.max(0, Math.min(100, e.currentPercentage + share));
    const newTotal = (newPct / 100) * clusterTotal;
    const split = splitBalanceChange(e, newTotal);
    return {
      ...e,
      currentPercentage: newPct,
      currentTotalBalance: newTotal,
      currentStableBalance: split.stableBalance,
      currentAssetBalance: split.assetBalance,
    };
  });
}

export function useDistributionState(
  subscriptions: SubscriptionSummary[],
  selectedExchange: Exchange | null,
) {
  const [entries, setEntries] = useState<DistributionEntry[]>(() =>
    buildEntries(subscriptions, selectedExchange),
  );

  useEffect(() => {
    setEntries(buildEntries(subscriptions, selectedExchange));
  }, [subscriptions, selectedExchange]);

  const clusterTotal = useMemo(() => {
    return entries.reduce((sum, e) => sum + e.originalTotalBalance, 0);
  }, [entries]);

  const hasChanges = useMemo(() => {
    return entries.some(
      (e) =>
        Math.abs(e.currentPercentage - e.originalPercentage) > 0.01 ||
        Math.abs(e.currentStableBalance - e.originalStableBalance) > 0.01 ||
        Math.abs(e.currentAssetBalance - e.originalAssetBalance) > 0.001,
    );
  }, [entries]);

  const changedEntries = useMemo(() => {
    return entries.filter(
      (e) =>
        Math.abs(e.currentStableBalance - e.originalStableBalance) > 0.01 ||
        Math.abs(e.currentAssetBalance - e.originalAssetBalance) > 0.001,
    );
  }, [entries]);

  const setPercentage = useCallback(
    (subscriptionId: number, newPct: number) => {
      setEntries((prev) => {
        const idx = prev.findIndex((e) => e.subscriptionId === subscriptionId);
        if (idx === -1) return prev;

        const entry = prev[idx];
        if (entry.isDisabled || entry.isLocked) return prev;

        const eligible = prev.filter(
          (e) =>
            !e.isLocked && !e.isDisabled && e.subscriptionId !== subscriptionId,
        );
        if (eligible.length === 0) return prev;

        // Cap: can't exceed own pct + sum of all eligible subs' pct
        const eligiblePctSum = eligible.reduce(
          (sum, e) => sum + e.currentPercentage,
          0,
        );
        const maxPct = entry.currentPercentage + eligiblePctSum;
        const clampedPct = Math.max(0, Math.min(maxPct, newPct));
        const delta = clampedPct - entry.currentPercentage;
        if (Math.abs(delta) < 0.01) return prev;

        const shares = distributeAcross(prev, eligible, delta);

        // Apply the changed sub first, then all redistributed subs
        const newTotal = (clampedPct / 100) * clusterTotal;
        const split = splitBalanceChange(entry, newTotal);
        let next = prev.map((e) =>
          e.subscriptionId === subscriptionId
            ? {
                ...e,
                currentPercentage: clampedPct,
                currentTotalBalance: newTotal,
                currentStableBalance: split.stableBalance,
                currentAssetBalance: split.assetBalance,
              }
            : e,
        );
        next = applyShares(next, shares, clusterTotal);
        return next;
      });
    },
    [clusterTotal],
  );

  const setTotalBalance = useCallback(
    (subscriptionId: number, newTotal: number) => {
      if (clusterTotal === 0) return;
      const newPct = (newTotal / clusterTotal) * 100;
      setPercentage(subscriptionId, newPct);
    },
    [clusterTotal, setPercentage],
  );

  const setStableBalance = useCallback(
    (subscriptionId: number, newStable: number) => {
      setEntries((prev) => {
        const idx = prev.findIndex((e) => e.subscriptionId === subscriptionId);
        if (idx === -1) return prev;

        const entry = prev[idx];
        const assetValueInStable =
          entry.currentAssetBalance * entry.latestPrice;
        const newTotal = Math.max(0, newStable) + assetValueInStable;

        if (entry.isLocked) {
          return prev.map((e) =>
            e.subscriptionId === subscriptionId
              ? {
                  ...e,
                  currentStableBalance: Math.max(0, newStable),
                  currentTotalBalance: newTotal,
                  currentPercentage:
                    clusterTotal > 0 ? (newTotal / clusterTotal) * 100 : 0,
                }
              : e,
          );
        }

        const rawPct = clusterTotal > 0 ? (newTotal / clusterTotal) * 100 : 0;

        const eligible = prev.filter(
          (e) =>
            !e.isLocked && !e.isDisabled && e.subscriptionId !== subscriptionId,
        );
        const eligiblePctSum = eligible.reduce(
          (sum, e) => sum + e.currentPercentage,
          0,
        );
        const maxPct = entry.currentPercentage + eligiblePctSum;
        const clampedPct = Math.max(0, Math.min(maxPct, rawPct));
        const delta = clampedPct - entry.currentPercentage;

        if (Math.abs(delta) < 0.01) {
          return prev.map((e) =>
            e.subscriptionId === subscriptionId
              ? {
                  ...e,
                  currentStableBalance: Math.max(0, newStable),
                  currentTotalBalance: newTotal,
                }
              : e,
          );
        }

        if (eligible.length === 0) {
          return prev.map((e) =>
            e.subscriptionId === subscriptionId
              ? {
                  ...e,
                  currentStableBalance: Math.max(0, newStable),
                  currentTotalBalance: newTotal,
                  currentPercentage: clampedPct,
                }
              : e,
          );
        }

        const shares = distributeAcross(prev, eligible, delta);
        let next = prev.map((e) =>
          e.subscriptionId === subscriptionId
            ? {
                ...e,
                currentStableBalance: Math.max(0, newStable),
                currentTotalBalance: newTotal,
                currentPercentage: clampedPct,
              }
            : e,
        );
        next = applyShares(next, shares, clusterTotal);
        return next;
      });
    },
    [clusterTotal],
  );

  const setAssetBalance = useCallback(
    (subscriptionId: number, newAsset: number) => {
      setEntries((prev) => {
        const idx = prev.findIndex((e) => e.subscriptionId === subscriptionId);
        if (idx === -1) return prev;

        const entry = prev[idx];
        const newAssetValueInStable = Math.max(0, newAsset) * entry.latestPrice;
        const newTotal = entry.currentStableBalance + newAssetValueInStable;

        if (entry.isLocked) {
          return prev.map((e) =>
            e.subscriptionId === subscriptionId
              ? {
                  ...e,
                  currentAssetBalance: Math.max(0, newAsset),
                  currentTotalBalance: newTotal,
                  currentPercentage:
                    clusterTotal > 0 ? (newTotal / clusterTotal) * 100 : 0,
                }
              : e,
          );
        }

        const rawPct = clusterTotal > 0 ? (newTotal / clusterTotal) * 100 : 0;

        const eligible = prev.filter(
          (e) =>
            !e.isLocked && !e.isDisabled && e.subscriptionId !== subscriptionId,
        );
        const eligiblePctSum = eligible.reduce(
          (sum, e) => sum + e.currentPercentage,
          0,
        );
        const maxPct = entry.currentPercentage + eligiblePctSum;
        const clampedPct = Math.max(0, Math.min(maxPct, rawPct));
        const delta = clampedPct - entry.currentPercentage;

        if (Math.abs(delta) < 0.01) {
          return prev.map((e) =>
            e.subscriptionId === subscriptionId
              ? {
                  ...e,
                  currentAssetBalance: Math.max(0, newAsset),
                  currentTotalBalance: newTotal,
                }
              : e,
          );
        }

        if (eligible.length === 0) {
          return prev.map((e) =>
            e.subscriptionId === subscriptionId
              ? {
                  ...e,
                  currentAssetBalance: Math.max(0, newAsset),
                  currentTotalBalance: newTotal,
                  currentPercentage: clampedPct,
                }
              : e,
          );
        }

        const shares = distributeAcross(prev, eligible, delta);
        let next = prev.map((e) =>
          e.subscriptionId === subscriptionId
            ? {
                ...e,
                currentAssetBalance: Math.max(0, newAsset),
                currentTotalBalance: newTotal,
                currentPercentage: clampedPct,
              }
            : e,
        );
        next = applyShares(next, shares, clusterTotal);
        return next;
      });
    },
    [clusterTotal],
  );

  const toggleLock = useCallback((subscriptionId: number) => {
    setEntries((prev) =>
      prev.map((e) =>
        e.subscriptionId === subscriptionId
          ? { ...e, isLocked: !e.isLocked }
          : e,
      ),
    );
  }, []);

  const reset = useCallback(() => {
    setEntries((prev) =>
      prev.map((e) =>
        e.isLocked
          ? e
          : {
              ...e,
              currentPercentage: e.originalPercentage,
              currentTotalBalance: e.originalTotalBalance,
              currentStableBalance: e.originalStableBalance,
              currentAssetBalance: e.originalAssetBalance,
            },
      ),
    );
  }, []);

  return {
    entries,
    clusterTotal,
    hasChanges,
    changedEntries,
    setPercentage,
    setTotalBalance,
    setStableBalance,
    setAssetBalance,
    toggleLock,
    reset,
  };
}
